import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, Star, Camera, X, Eraser } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select as UISelect, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { API_V2, authOnly, imgUrl } from './checklistUtils';

// ── Star Rating Selector ────────────────────────────────────────────────────

export const StarSelector = ({ value, max = 10, onChange, readOnly = false }) => (
  <div className="flex gap-0.5 flex-wrap" data-testid="star-selector">
    {Array.from({ length: max }).map((_, i) => (
      <button key={i} onClick={() => !readOnly && onChange?.(i + 1)} disabled={readOnly}
        className={`transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        data-testid={`star-${i + 1}`}>
        <Star className={`w-6 h-6 sm:w-7 sm:h-7 ${i < (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      </button>
    ))}
    {value != null && <span className="text-sm font-semibold text-slate-600 ml-1 self-center">{value}/{max}</span>}
  </div>
);

// ── Foto Thumbnail + Lightbox (gedeeld) ─────────────────────────────────────
// Grotere, klikbare thumbnail. Klik opent een full-size lightbox (object-contain,
// max 90vw/90vh). Sluiten via kruisje, klik-op-overlay of Escape. Optionele
// onRemove toont een verwijder-knop (X) die de lightbox NIET triggert.

export const FotoThumb = ({ url, onRemove, alt = '' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const src = imgUrl(url);
  return (
    <>
      <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-lg border overflow-hidden group">
        <button type="button" onClick={() => setOpen(true)}
          className="block w-full h-full cursor-zoom-in"
          aria-label="Foto vergroten" data-testid="foto-thumb-open">
          <img src={src} alt={alt}
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
          <span className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" />
        </button>
        {onRemove && (
          <button type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute top-0 right-0 z-10 bg-red-500 text-white rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Foto verwijderen" data-testid="foto-thumb-remove">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setOpen(false)} data-testid="foto-lightbox">
          <button type="button" onClick={() => setOpen(false)}
            className="absolute top-4 right-4 text-white/90 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
            aria-label="Sluiten" data-testid="foto-lightbox-close">
            <X className="w-6 h-6" />
          </button>
          <img src={src} alt={alt} onClick={(e) => e.stopPropagation()}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            data-testid="foto-lightbox-image" />
        </div>
      )}
    </>
  );
};

// ── Photo Upload V2 ─────────────────────────────────────────────────────────

export const FotoUploadV2 = ({ urls = [], onChange, max = 5 }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    const resterend = max - urls.length;
    if (files.length === 0 || resterend <= 0) {
      if (ref.current) ref.current.value = '';
      return;
    }
    // Respecteer resterende slots: upload alleen wat past
    const teUploaden = files.slice(0, resterend);
    if (files.length > resterend) {
      toast.warning(`Maximaal ${max} foto's — alleen de eerste ${resterend} toegevoegd`);
    }
    setUploading(true);
    const nieuweUrls = [];
    let mislukt = 0;
    for (const file of teUploaden) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_V2}/checklists/upload-foto`, { method: 'POST', headers: authOnly(), body: fd });
        if (res.ok) {
          const { url } = await res.json();
          nieuweUrls.push(url);
        } else {
          mislukt += 1;
        }
      } catch {
        mislukt += 1; // één kapot bestand mag de batch niet breken
      }
    }
    if (nieuweUrls.length > 0) onChange([...urls, ...nieuweUrls]);
    if (mislukt > 0) toast.error(`${mislukt} foto('s) niet geüpload`);
    setUploading(false);
    if (ref.current) ref.current.value = '';
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {urls.map((url, i) => (
        <FotoThumb key={i} url={url} onRemove={() => onChange(urls.filter((_, j) => j !== i))} />
      ))}
      {urls.length < max && (
        <button onClick={() => ref.current?.click()} disabled={uploading}
          className="w-28 h-28 sm:w-32 sm:h-32 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 flex items-center justify-center text-slate-400"
          data-testid="foto-upload-btn">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-6 h-6" />}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={handleFile} />
    </div>
  );
};

// ── Ja/Nee Toggle ───────────────────────────────────────────────────────────

export const JaNeeToggle = ({ value, onChange, readOnly = false }) => {
  const ja = value === true;
  const nee = value === false;
  return (
    <div className="flex gap-0.5 flex-shrink-0">
      <button onClick={() => !readOnly && onChange(ja ? null : true)} disabled={readOnly}
        className={`px-3 py-1.5 text-xs font-bold rounded-l-lg border transition-all min-w-[40px] ${
          ja ? 'bg-green-500 text-white border-green-500' : 'bg-white text-slate-500 border-slate-300 hover:border-green-300'
        } ${readOnly ? 'cursor-default' : ''}`} data-testid="btn-ja">Ja</button>
      <button onClick={() => !readOnly && onChange(nee ? null : false)} disabled={readOnly}
        className={`px-3 py-1.5 text-xs font-bold rounded-r-lg border transition-all min-w-[40px] ${
          nee ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-500 border-slate-300 hover:border-red-300'
        } ${readOnly ? 'cursor-default' : ''}`} data-testid="btn-nee">Nee</button>
    </div>
  );
};

// ── Nummer input ───────────────────────────────────────────────────────────

export const NummerInput = ({ item, value, onChange, readOnly = false }) => {
  if (readOnly) {
    const display = value == null || value === ''
      ? '-'
      : `${value}${item.unit ? ' ' + item.unit : ''}`;
    return <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">{display}</p>;
  }
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={value ?? ''}
        min={item.min_waarde}
        max={item.max_waarde}
        step="any"
        onChange={e => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
        placeholder={
          item.min_waarde != null && item.max_waarde != null
            ? `${item.min_waarde} – ${item.max_waarde}`
            : 'Getal'
        }
        className="text-sm max-w-[200px]"
        data-testid={`input-nummer-${item.item_id}`}
      />
      {item.unit && <span className="text-sm text-slate-500 flex-shrink-0">{item.unit}</span>}
    </div>
  );
};

// ── Radio ──────────────────────────────────────────────────────────────────

export const RadioOptions = ({ item, value, onChange, readOnly = false }) => {
  const opties = item.opties || [];
  if (readOnly) {
    const selected = opties.find(o => o.key === value);
    return (
      <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">
        {selected ? selected.label : '-'}
      </p>
    );
  }
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap" data-testid={`radio-${item.item_id}`}>
      {opties.map(o => {
        const selected = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(selected ? null : o.key)}
            className={`px-3 py-2 text-sm rounded-lg border transition-all text-left ${
              selected
                ? 'bg-[#1f2548] text-white border-[#1f2548]'
                : 'bg-white text-slate-700 border-slate-300 hover:border-[#1f2548]/50'
            }`}
            data-testid={`radio-option-${item.item_id}-${o.key}`}
          >
            <span className={`inline-block w-3 h-3 rounded-full border mr-2 ${
              selected ? 'bg-white border-white' : 'bg-white border-slate-400'
            }`}>
              {selected && <span className="block w-1.5 h-1.5 rounded-full bg-[#1f2548] m-[3px]" />}
            </span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

// ── Keuzelijst (dropdown) ──────────────────────────────────────────────────

export const KeuzelijstDropdown = ({ item, value, onChange, readOnly = false }) => {
  const opties = item.opties || [];
  if (readOnly) {
    const selected = opties.find(o => o.key === value);
    return (
      <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">
        {selected ? selected.label : '-'}
      </p>
    );
  }
  return (
    <UISelect value={value || ''} onValueChange={v => onChange(v || null)}>
      <SelectTrigger className="max-w-[300px] text-sm" data-testid={`select-${item.item_id}`}>
        <SelectValue placeholder="Maak een keuze..." />
      </SelectTrigger>
      <SelectContent>
        {opties.map(o => (
          <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </UISelect>
  );
};

// ── Keuzelijst meervoud (checkboxes) ───────────────────────────────────────

export const KeuzelijstMeervoud = ({ item, value, onChange, readOnly = false }) => {
  const opties = item.opties || [];
  const selected = Array.isArray(value) ? value : [];
  if (readOnly) {
    const labels = opties.filter(o => selected.includes(o.key)).map(o => o.label);
    return (
      <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">
        {labels.length ? labels.join(', ') : '-'}
      </p>
    );
  }
  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter(k => k !== key)
      : [...selected, key];
    onChange(next);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`multi-${item.item_id}`}>
      {opties.map(o => (
        <label
          key={o.key}
          className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1.5 rounded hover:bg-slate-50"
          data-testid={`multi-option-${item.item_id}-${o.key}`}
        >
          <Checkbox
            checked={selected.includes(o.key)}
            onCheckedChange={() => toggle(o.key)}
          />
          <span className="text-slate-700">{o.label}</span>
        </label>
      ))}
    </div>
  );
};

// ── Kort tekstveld met character counter + max_lengte ──────────────────────

export const TekstKortInput = ({ item, value, onChange, readOnly = false }) => {
  if (readOnly) {
    return <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2">{value || '-'}</p>;
  }
  const max = item.max_lengte;
  const len = (value || '').length;
  const overMin = item.min_lengte != null && len > 0 && len < item.min_lengte;
  return (
    <div className="space-y-1">
      <Input
        value={value || ''}
        maxLength={max || undefined}
        onChange={e => onChange(e.target.value)}
        placeholder="Typ hier..."
        className="text-sm"
        data-testid={`input-${item.item_id}`}
      />
      {(max || item.min_lengte) && (
        <div className="flex justify-between text-xs text-slate-400 px-1">
          {overMin && (
            <span className="text-amber-600">min. {item.min_lengte} tekens</span>
          )}
          <span className="ml-auto">
            {max ? `${len}/${max}` : `${len} tekens`}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Handtekening (canvas) ──────────────────────────────────────────────────

export const HandtekeningPad = ({ item, value, onChange, readOnly = false }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1f2548';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  if (readOnly || value) {
    return value ? (
      <div className="inline-block rounded border border-slate-200 bg-white p-1">
        <img
          src={imgUrl(value)}
          alt="Handtekening"
          className="max-w-[300px] max-h-[120px] object-contain"
          data-testid={`signature-image-${item.item_id}`}
        />
        {!readOnly && (
          <div className="mt-1">
            <button
              onClick={() => onChange(null)}
              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              data-testid={`signature-clear-${item.item_id}`}
            >
              <Eraser className="w-3 h-3" /> Opnieuw tekenen
            </button>
          </div>
        )}
      </div>
    ) : (
      <p className="text-sm text-slate-400 italic">Niet ondertekend</p>
    );
  }

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStroke(true);
  };
  const stop = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
  };

  const save = async () => {
    const canvas = canvasRef.current;
    if (!hasStroke) { toast.error('Teken eerst een handtekening'); return; }
    setUploading(true);
    try {
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('canvas_empty');
      const fd = new FormData();
      fd.append('file', new File([blob], `signature-${Date.now()}.png`, { type: 'image/png' }));
      const res = await fetch(`${API_V2}/checklists/upload-foto`, {
        method: 'POST', headers: authOnly(), body: fd,
      });
      if (!res.ok) throw new Error('upload_failed');
      const { url } = await res.json();
      onChange(url);
      toast.success('Handtekening opgeslagen');
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`signature-pad-${item.item_id}`}>
      <canvas
        ref={canvasRef}
        width={400}
        height={140}
        className="border-2 border-dashed border-slate-300 rounded-lg bg-white w-full max-w-[400px] touch-none"
        onMouseDown={start}
        onMouseMove={draw}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={draw}
        onTouchEnd={stop}
      />
      <div className="flex gap-2">
        <button
          onClick={clear}
          type="button"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 text-slate-600"
          data-testid={`signature-clear-btn-${item.item_id}`}
        >
          Wissen
        </button>
        <button
          onClick={save}
          type="button"
          disabled={uploading || !hasStroke}
          className="text-xs px-3 py-1.5 bg-[#1f2548] text-white rounded hover:bg-[#1f2548]/90 disabled:opacity-40 flex items-center gap-1"
          data-testid={`signature-save-btn-${item.item_id}`}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Opslaan
        </button>
      </div>
    </div>
  );
};

// ── Koptekst (display-only) ────────────────────────────────────────────────

export const KoptekstBlock = ({ item }) => (
  <div
    className="py-2 border-t border-slate-200 mt-1"
    data-testid={`koptekst-${item.item_id}`}
  >
    <h4 className="font-semibold text-base text-[#1f2548]">{item.naam}</h4>
  </div>
);

// ── Item Renderer ───────────────────────────────────────────────────────────

export const ItemRenderer = ({ item, schaal, onUpdate, readOnly = false }) => {
  const updateVal = (v) => onUpdate?.({ ...item, waarde: v });
  const isPhotoOnly = item.type === 'foto';
  const t = item.type;

  // Koptekst is display-only: eigen compact block, geen label/input
  if (t === 'koptekst') {
    return <KoptekstBlock item={item} />;
  }

  return (
    <div className="py-3 space-y-2" data-testid={`item-${item.item_id}`}>
      <div className="flex items-start gap-3">
        {t === 'checkbox' && (
          <JaNeeToggle value={item.waarde} onChange={updateVal} readOnly={readOnly} />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm text-slate-700">
            {item.naam}
            {item.verplicht && <span className="text-red-500 ml-0.5">*</span>}
          </span>
        </div>
      </div>
      {t === 'tekst_kort' && (
        <TekstKortInput item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'tekst_lang' && (
        readOnly
          ? <p className="text-sm text-slate-700 bg-slate-50 rounded px-3 py-2 whitespace-pre-wrap">{item.waarde || '-'}</p>
          : <Textarea value={item.waarde || ''} onChange={e => updateVal(e.target.value)} placeholder="Typ hier..." rows={3} className="text-sm" data-testid={`textarea-${item.item_id}`} />
      )}
      {t === 'beoordeling' && (
        <StarSelector value={item.waarde} max={schaal || 10} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'nummer' && (
        <NummerInput item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'radio' && (
        <RadioOptions item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'keuzelijst' && (
        <KeuzelijstDropdown item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'keuzelijst_meervoud' && (
        <KeuzelijstMeervoud item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {t === 'handtekening' && (
        <HandtekeningPad item={item} value={item.waarde} onChange={updateVal} readOnly={readOnly} />
      )}
      {(item.foto_toegestaan || isPhotoOnly) && !readOnly && (
        <FotoUploadV2 urls={item.foto_urls || []} onChange={urls => onUpdate?.({ ...item, foto_urls: urls })} max={3} />
      )}
      {readOnly && item.foto_urls?.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {item.foto_urls.map((url, i) => (
            <FotoThumb key={i} url={url} />
          ))}
        </div>
      )}
      {readOnly && isPhotoOnly && (!item.foto_urls || item.foto_urls.length === 0) && (
        <p className="text-sm text-slate-400 italic">Geen foto's</p>
      )}
    </div>
  );
};
